import { Attachment, Button } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { AttachmentType } from '@features/attachments/enums/attachmentType'
import { isAttachmentBlocked } from '@features/attachments/utils/isAttachmentBlocked'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { QueryKeys } from '../../../../enums/queryKeys'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { reactRoute } from '../../../../utils/routing/reactRoute'

interface ReceiptButtonProps {
  attachment: Attachment
}

export const ReceiptButton = ({ attachment }: ReceiptButtonProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const queryClient = useQueryClient()
  const newFromAttachmentRoute = reactRoute.organization.bills.getNewBillFromAttachmentRoute(attachment.id)
  const { url: billsNewFromAttachmentUrl } = useOrganizationViewUrl(newFromAttachmentRoute)

  const isEnabled = isAuthorized(Scope.BillWrite)
  const buttonLabel = useMemo(() => {
    if (isAttachmentBlocked(attachment)) {
      return t('receipt.list.blocked')
    }
    return attachment.type === AttachmentType.EDocument ? t('receipt.manage_e_invoice') : t('receipt.manage_upload')
  }, [attachment, t])

  // Handlers

  const handleClick = useCallback(() => {
    track(ExpensesTrackingEvents.AttachmentManageButtonClick)
    queryClient.setQueryData([QueryKeys.Attachment, attachment.id], { attachment })
  }, [attachment, queryClient, track])

  return (
    <Button
      disabled={!isEnabled}
      fullWidth
      onClick={handleClick}
      variant="outlined"
      size="md"
      to={billsNewFromAttachmentUrl}
    >
      {buttonLabel}
    </Button>
  )
}
